import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { env } from '../../../env';
import { ApiRequestStatus } from '../../../model/api-request';
import { GlobalActions } from '../../../model/global-actions';
import appRoutes from '../../../routing/app-routes';
import {
  finalizeOnboardingSliceActions,
  getCurrentDocumentVersion,
  LoginState,
  updateMigrato,
} from '../../../store';
import { getStatusUtenza, updateUsername } from '../../../store/data/user';
import { signatureArea } from '../../../store/features/profile/signature-area/signature-area-thunk';
import {
  login,
  resetLoginState,
} from '../../../store/features/user-access/login/login-slice';
import { messagesForLoginError } from '../../../store/features/user-access/login/login-utils';
import { useAppDispatch, useAppSelector } from '../../redux';
import { AcceptanceDocumentType, LoginMessage } from '../../../api/generated';

export interface LoginPageLogicCallbacks {
  username: string;
  password: string;
  isRememberMe: boolean;
  loginRequestStatus: ApiRequestStatus;
  showPasswordInput: boolean;
  handleSubmit: () => void;
  setUsername: (username?: string) => void;
  setPassword: (pwd?: string) => void;
  rememberMeToggle: () => void;
  informativeMessages?: LoginMessage[];
}


export const useLoginPageLogic = (
  actions: GlobalActions
): LoginPageLogicCallbacks => {
  const { loginRequestStatus, loginError, isFirstAccess, config }: LoginState =
    useAppSelector((state) => state.features.login);

  const isProspect: boolean = useAppSelector(
    (state) => state.data.user.isProspect
  );

  const isFiduciaria: boolean = useAppSelector(
    (state) => state.data.user.fiduciaria
  );

  const usernameFromLogin: string = useAppSelector(
    (state) => state.data.user.username
  );

  const referralFromLogin: string = useAppSelector(
    (state) => state.data.user.referral
  );

  const { formatMessage } = useIntl();

  const dispatch = useAppDispatch();
  const { navigate, showToast, closeToast, getStorage } = actions;

  const key = 'username';

  /** Form management */
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isRememberMe, setIsRememberMe] = useState(false);

  /** Error management */
  useEffect(() => {
    if (loginError && loginError !== 'USER_BLOCKED') {
      const errorMessages = messagesForLoginError(loginError);
      const notMigratedUser = loginError === 'NOT_MIGRATED_USER';

      if (notMigratedUser) {
        dispatch(updateMigrato(false));
        navigate(appRoutes.FirstAccess, { replace: true });
        return;
      }

      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });

      dispatch(resetLoginState());
      if (env.appModule === 'web') {
        sessionStorage.clear();
      }
    }
  }, [
    closeToast,
    dispatch,
    formatMessage,
    loginError,
    navigate,
    showToast,
    username,
  ]);

  useEffect(() => {
    const handleLoginNavigation = async () => {
      if (loginRequestStatus === 'succeeded' && usernameFromLogin) {
        if (env.appModule === 'web') {
          sessionStorage.clear();
        }

        if (isRememberMe) {
          getStorage().storeValue(key, username);
        } else {
          getStorage().deleteValue(key);
        }

        let targetRoute = appRoutes.Dashboard;

        if (isFirstAccess && isProspect) {
          dispatch(
            finalizeOnboardingSliceActions.setReferral(referralFromLogin)
          );
          targetRoute = appRoutes.SetPassword;
        } else if (isFirstAccess) {
          dispatch(getStatusUtenza());
          dispatch(signatureArea(undefined));
          targetRoute = appRoutes.FirstAccess;
        } else if (isFiduciaria) {
          targetRoute = appRoutes.SetContext;
        } else if (env.appModule === 'mobile') {
          try {
            const resultAction = await dispatch(
              getCurrentDocumentVersion(
                AcceptanceDocumentType.TermsAndConditions
              )
            );
            if (getCurrentDocumentVersion.fulfilled.match(resultAction)) {
              const doc = resultAction.payload;
              if (doc && !doc.accepted) {
                targetRoute = appRoutes.InformativeUserLogin;
              }
            }
          } catch (error) {
            console.error('Error during document acceptance check:', error);
          }
        }

        dispatch(resetLoginState());
        navigate(targetRoute, { replace: true });
      }
    };

    handleLoginNavigation();
  }, [
    loginRequestStatus,
    dispatch,
    navigate,
    isFirstAccess,
    getStorage,
    isRememberMe,
    username,
    usernameFromLogin,
    isProspect,
    referralFromLogin,
    isFiduciaria,
  ]);

  useEffect(() => {
    if (loginError) {
      if (loginError === 'USER_BLOCKED') {
        navigate(appRoutes.UnlockUserRequest, { replace: false });
      }
    }
    dispatch(resetLoginState());
    if (env.appModule === 'web') {
      sessionStorage.clear();
    }
  }, [dispatch, loginError, navigate]);

  useEffect(() => {
    getStorage()
      .readValue(key)
      .then((storedUsername) => {
        if (storedUsername) {
          setIsRememberMe(!isRememberMe);
          setUsername(storedUsername);
        }
      })
      .catch(() => {
        // No need to execute any operation, the username remains empty
      });
    // No need to pass dependencies, because it must be executed only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rememberMeToggle = useCallback(() => {
    setIsRememberMe(!isRememberMe);
    if (!isRememberMe) {
      getStorage().deleteValue(key);
    }
  }, [getStorage, isRememberMe]);

  const handleSubmit = useCallback(() => {
    const usernameClean = username.trim();
    dispatch(
      login({
        username: usernameClean,
        password,
      })
    );
    dispatch(updateUsername(usernameClean));
  }, [password, username, dispatch]);

  return {
    username,
    password,
    isRememberMe,
    loginRequestStatus,
    handleSubmit,
    setUsername,
    setPassword,
    rememberMeToggle,
    showPasswordInput: true,
    informativeMessages: config?.loginMessages || [],
  };
};
